import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Sector,
  Cell,
  Tooltip
} from 'recharts';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import './StatusCharts.scss';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';

export default function StatusCharts(props) {
  const className = 'issueDashboards__statusCharts';
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Build data (exclude any undefined status objects, keep color)
  const pieData = useMemo(() => {
    if (!Array.isArray(props.ticketStatusList)) return [];
    return props.ticketStatusList
      .filter(s => s?.status?.title && typeof s?.count === 'number')
      .map(s => ({
        name: s.status.title,
        value: s.count,
        color: s.status.color || '#5f6368',
        raw: s
      }));
  }, [props.ticketStatusList]);

  const total = useMemo(
    () => pieData.reduce((a, b) => a + (b.value || 0), 0),
    [pieData]
  );

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 250);
    return () => clearTimeout(t);
  }, [pieData]);

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 700);
    // Optional: parent can trigger real refresh
    props.reloadData && props.reloadData();
  };

  const onSliceEnter = (_, i) => setActiveIndex(i);

  const applyFilter = useCallback(
    (statusObj) => {
      props.applyFilterForTicketsPage &&
        statusObj &&
        props.applyFilterForTicketsPage([statusObj.raw]);
    },
    [props]
  );

  const activeShape = (p) => {
    const {
      cx, cy, innerRadius, outerRadius,
      startAngle, endAngle, payload, value, percent
    } = p;
    return (
      <g>
        <text x={cx} y={cy - 12} textAnchor="middle" className={className + '__centerTitle'}>
          {payload.name}
        </text>
        <text x={cx} y={cy + 6} textAnchor="middle" className={className + '__centerValue'}>
          {value} ({(percent * 100).toFixed(0)}%)
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={payload.color}
          stroke="var(--color-darkBorder)"
          strokeWidth={2}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 12}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={payload.color}
          opacity={0.25}
        />
      </g>
    );
  };

  const emptyState = (
    <div className={className + '__empty'}>
      <ErrorOutlineIcon />
      <span>No ticket status data</span>
    </div>
  );

  const loader = (
    <div className={className + '__loader'}>
      <Skeleton variant="circular" width={220} height={220} />
      <div className={className + '__loader__legend'}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={90} height={24} style={{ borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );

  const chart = (
    <div className={className + '__chartWrap'}>
      <div className={className + '__chart'}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              innerRadius="55%"
              outerRadius="75%"
              activeIndex={activeIndex}
              activeShape={activeShape}
              onMouseEnter={onSliceEnter}
              onClick={(_, i) => applyFilter(pieData[i])}
              paddingAngle={1}
            >
              {pieData.map((d, i) => (
                <Cell key={d.name + i} fill={d.color} stroke={d.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(val, name, item) => [
                val,
                `${name} (${((val / total) * 100).toFixed(1)}%)`
              ]}
              contentStyle={{
                backgroundColor: 'var(--color-componentBackground)',
                color: 'var(--color-text)',
                borderRadius: 12,
                border: '1px solid var(--color-darkBorder)',
                boxShadow: '0 12px 24px var(--color-shadow)'
              }}
              labelStyle={{ color: 'var(--color-text)' }}
              itemStyle={{ color: 'var(--color-primaryGrey)' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <div className={className}>
      <div className={className + '__header'}>
        <div className={className + '__header__left'}>
          <Typography variant="subtitle1" className={className + '__title'}>
            Ticket Status Distribution
          </Typography>
          <Typography className={className + '__subtitle'}>
            Color-coded breakdown of all tickets
          </Typography>
        </div>
        <div className={className + '__header__actions'}>
          <Chip
            label={total + ' total'}
            size="medium"
            className={className + '__totalChip'}
          />
          <IconButton
            size="medium"
            onClick={handleRefresh}
            disabled={refreshing}
            className={refreshing ? 'rotating' : ''}
          >
            <RefreshIcon fontSize="medium" />
          </IconButton>
        </div>
      </div>
      {loading ? loader : (pieData.length ? chart : emptyState)}
    </div>
  );
}
